import React, { useMemo } from 'react';
import { Button } from '../common';
import { Card } from '../common/Card';
import { Heading, Text, Typography } from '../common/Typography';
import { getGenderTextColor } from '../../utils/genderColors';
import { formatPlayerName } from '../../utils/nameUtils';
import { formatTournamentEntryLabel } from '../../utils/tournamentFormat';

/**
 * Shows tournament highlights - leaders, recent results, upcoming matches
 * For member view to see what's happening
 */
const TournamentHighlights = ({ 
  tournament,
  groupStandings = [], 
  matches = [],
  onViewGroup,
  onViewKnockout
}) => {
  const isSingles = tournament?.game_type_id === 7 || tournament?.game_type_id === 5;

  // Get group leaders
  const groupLeaders = useMemo(() => {
    return groupStandings
      .map(group => {
        const leader = group.standings?.[0];
        if (!leader) return null;
        return {
          groupName: group.groupName,
          team: leader.team,
          wins: leader.wins,
          losses: leader.losses,
          pointDifference: leader.pointDifference
        };
      })
      .filter(Boolean);
  }, [groupStandings]);

  // Get recent completed matches (last 5)
  const recentMatches = useMemo(() => {
    return matches
      .filter(m => 
        ['completed', 'walkover', 'forfeit'].includes(m.status) ||
        (m.games && m.games.length > 0)
      )
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      .slice(0, 5);
  }, [matches]);

  // Get upcoming matches (next 5 unscored)
  const upcomingMatches = useMemo(() => {
    return matches
      .filter(m => 
        m.status === 'scheduled' &&
        (!m.games || m.games.length === 0)
      )
      .slice(0, 5);
  }, [matches]);

  const hasKnockout = matches.some(m => m.round !== 'group');

  if (groupLeaders.length === 0 && recentMatches.length === 0 && upcomingMatches.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <Heading level={3} className="text-lg mb-4">
        Tournament Highlights
      </Heading>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Group Leaders */}
        {groupLeaders.length > 0 && (
          <Card variant="default" size="medium">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="text-xl">👑</div>
                <Typography variant="strong" className="text-sm">
                  Group Leaders
                </Typography>
              </div>
              {onViewGroup && (
                <Button
                  variant="ghost"
                  size="small"
                  onClick={onViewGroup}
                  className="text-xs"
                >
                  View All
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {groupLeaders.map((leader, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-2 bg-surface-subtle rounded-md"
                >
                  <div className="flex-1 min-w-0">
                    <Text className="text-xs text-text-muted mb-0.5">
                      Group {leader.groupName}
                    </Text>
                    <Typography variant="body" className="text-sm truncate">
                      {leader.team?.player2 ? (
                        <>
                          <span className={getGenderTextColor(leader.team?.player1?.gender || '')}>
                            {formatPlayerName(leader.team?.player1)}
                          </span>
                          {' & '}
                          <span className={getGenderTextColor(leader.team?.player2?.gender || '')}>
                            {formatPlayerName(leader.team?.player2)}
                          </span>
                        </>
                      ) : (
                        <span className={getGenderTextColor(leader.team?.player1?.gender || '')}>
                          {formatTournamentEntryLabel(leader.team, true)}
                        </span>
                      )}
                    </Typography>
                  </div>
                  <div className="text-right ml-3 shrink-0">
                    <div className="text-sm font-bold text-success">
                      {leader.wins}-{leader.losses}
                    </div>
                    <Text className="text-xs text-text-muted">
                      +{leader.pointDifference}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Recent Results */}
        {recentMatches.length > 0 && (
          <Card variant="default" size="medium">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="text-xl">📊</div>
                <Typography variant="strong" className="text-sm">
                  Recent Results
                </Typography>
              </div>
            </div>
            <div className="space-y-2">
              {recentMatches.map((match, index) => {
                const team1Won = match.winner_entry_id === match.entry1_id;
                const team2Won = match.winner_entry_id === match.entry2_id;
                const isWalkover = match.status === 'walkover' || match.status === 'forfeit';

                return (
                  <div 
                    key={match.id}
                    className="p-2 bg-surface-subtle rounded-md text-xs"
                  >
                    <Text className="text-text-muted mb-1">
                      {match.round === 'group' ? `Group ${match.group_name || ''}` :
                       match.round === 'final' ? 'Final' :
                       match.round === 'semi_final' ? 'Semi Final' :
                       match.round === 'quarter_final' ? 'Quarter Final' :
                       'Knockout'}
                    </Text>
                    <div className="flex items-center justify-between">
                      <div className={`flex-1 truncate ${team1Won ? 'font-bold text-success' : ''}`}>
                        {formatTournamentEntryLabel(match.team1, isSingles)}
                      </div>
                      <div className="mx-2 text-text-muted shrink-0">
                        {isWalkover ? (
                          <span className="text-xs">W/O</span>
                        ) : (
                          <span>vs</span>
                        )}
                      </div>
                      <div className={`flex-1 truncate text-right ${team2Won ? 'font-bold text-success' : ''}`}>
                        {formatTournamentEntryLabel(match.team2, isSingles)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Upcoming Matches */}
        {upcomingMatches.length > 0 && (
          <Card variant="default" size="medium">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="text-xl">📅</div>
                <Typography variant="strong" className="text-sm">
                  Upcoming Matches
                </Typography>
              </div>
              {hasKnockout && onViewKnockout && (
                <Button
                  variant="ghost"
                  size="small"
                  onClick={onViewKnockout}
                  className="text-xs"
                >
                  View Bracket
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {upcomingMatches.map((match, index) => (
                <div 
                  key={match.id}
                  className="p-2 bg-primary-light/10 border border-primary/20 rounded-md text-xs"
                >
                  <Text className="text-text-muted mb-1">
                    {match.round === 'group' ? `Group ${match.group_name || ''}` :
                     match.round === 'final' ? 'Final' :
                     match.round === 'semi_final' ? 'Semi Final' :
                     match.round === 'quarter_final' ? 'Quarter Final' :
                     'Knockout'}
                  </Text>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 truncate font-medium">
                      {formatTournamentEntryLabel(match.team1, isSingles)}
                    </div>
                    <div className="mx-2 text-text-muted shrink-0">vs</div>
                    <div className="flex-1 truncate text-right font-medium">
                      {formatTournamentEntryLabel(match.team2, isSingles)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TournamentHighlights;
